import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const FREE_FEATURES = ["10 folders", "30 decks", "250 flashcards", "Flip-card study mode"];
const PREMIUM_FEATURES = ["Unlimited folders", "Unlimited decks", "Unlimited flashcards", "Flip-card study mode", "Priority updates"];

export function UpgradeDialog({ open, onOpenChange, plan, usage }) {
  const [loading, setLoading] = useState(false);

  const upgrade = async () => {
    setLoading(true);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight text-2xl flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[#003366]" /> Upgrade to Revora Premium
          </DialogTitle>
          <DialogDescription>Unlock unlimited folders, decks, and flashcards.</DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 pt-2">
          <div className="rounded-2xl border border-slate-200 p-6">
            <p className="text-sm font-semibold uppercase tracking-widest text-slate-400">Free</p>
            <p className="font-display font-extrabold text-3xl text-slate-900 mt-2">£0</p>
            <p className="text-slate-400 text-sm">forever</p>
            <ul className="mt-5 space-y-2.5">
              {FREE_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-600">
                  <Check className="h-4 w-4 text-slate-400" /> {f}
                </li>
              ))}
            </ul>
            {plan === "free" && (
              <p className="mt-5 text-xs text-slate-400" data-testid="current-plan-note">Your current plan</p>
            )}
          </div>

          <div className="rounded-2xl border-2 border-[#003366] p-6 relative bg-[#003366]/[0.03]">
            <span className="absolute -top-3 left-6 bg-[#003366] text-white text-xs font-medium px-3 py-1 rounded-full">Recommended</span>
            <p className="text-sm font-semibold uppercase tracking-widest text-[#003366]">Premium</p>
            <p className="font-display font-extrabold text-3xl text-slate-900 mt-2">£2<span className="text-base font-normal text-slate-400">/month</span></p>
            <p className="text-slate-400 text-sm">billed monthly</p>
            <ul className="mt-5 space-y-2.5">
              {PREMIUM_FEATURES.map((f) => (
                <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                  <Check className="h-4 w-4 text-[#003366]" /> {f}
                </li>
              ))}
            </ul>
            {plan === "premium" ? (
              <p className="mt-6 text-sm font-medium text-[#003366]" data-testid="premium-active-note">✓ Active — you're all set</p>
            ) : (
              <Button data-testid="upgrade-checkout-btn" onClick={upgrade} disabled={loading}
                className="mt-6 w-full h-11 rounded-full bg-[#003366] hover:bg-[#00284d] text-white">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Upgrade for £2/mo"}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
